import React from 'react';
import { classNames } from '@/lib/utils';

// Tipos de variantes do botão
type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    isLoading?: boolean;
    fullWidth?: boolean;
    children: React.ReactNode;
}

// Estilos por variante
const variantStyles: Record<ButtonVariant, string> = {
    primary: 'bg-brand-primary hover:bg-brand-hover text-content-inverse focus:ring-brand-primary',
    secondary: 'bg-white border border-ui-border text-content-body hover:bg-brand-surface focus:ring-brand-primary',
    danger: 'bg-status-danger hover:bg-red-700 text-content-inverse focus:ring-status-danger',
    ghost: 'bg-transparent hover:bg-gray-100 text-content-body focus:ring-gray-300',
};

// Estilos por tamanho
const sizeStyles: Record<ButtonSize, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
};

export function Button({
    variant = 'primary',
    size = 'md',
    isLoading = false,
    fullWidth = false,
    disabled,
    className,
    children,
    ...props
}: ButtonProps) {
    const isDisabled = disabled || isLoading;

    return (
        <button
            className={classNames(
                // Base styles
                'inline-flex items-center justify-center font-medium rounded-lg',
                'transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                // Variant
                variantStyles[variant],
                // Size
                sizeStyles[size],
                // Full width
                fullWidth && 'w-full',
                // Disabled
                isDisabled && 'opacity-50 cursor-not-allowed',
                // Custom
                className
            )}
            disabled={isDisabled}
            {...props}
        >
            {isLoading && (
                <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    />
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                </svg>
            )}
            {children}
        </button>
    );
}

export default Button;
